/**
 * API Client Setup
 * Configured Ky instance with authentication using Clerk
 */

import ky from 'ky';
import { createAuthRepository } from '@/persistence';

// Get API URL from environment
const API_URL = import.meta.env.VITE_AI_API_URL || 'https://ai-api-dev.auctuscognita.ai';

// Auth repository instance
const authRepo = createAuthRepository();

/**
 * Get current auth token from Clerk via auth repository
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const token = await authRepo.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Configured Ky client with authentication
 */
export const kyClient = ky.create({
  prefixUrl: API_URL,
  timeout: 30000, // 30 seconds
  retry: {
    limit: 2,
    methods: ['get', 'post'],
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        // Add auth token to requests
        const token = await getAuthToken();
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }

        // Ensure content type is set
        if (!request.headers.has('Content-Type')) {
          request.headers.set('Content-Type', 'application/json');
        }
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        // Auto-refresh token on 401
        if (response.status === 401) {
          try {
            // Force token refresh
            const newToken = await authRepo.getIdToken(true);

            if (newToken) {
              // Retry request with new token
              request.headers.set('Authorization', `Bearer ${newToken}`);
              return ky(request);
            }
          } catch (error) {
            console.error('Error refreshing token:', error);
          }
        }

        return response;
      },
    ],
  },
});
